import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { propertiesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const documentLabels = {
  matricula: 'Matrícula do Imóvel',
  escritura: 'Escritura Pública',
  iptu: 'IPTU',
  certidaoNegativa: 'Certidão Negativa de Débitos',
  certidaoOnus: 'Certidão de Ônus Reais',
  habiteseART: 'Habite-se / ART',
  laudoAvaliacao: 'Laudo de Avaliação',
  plantaBaixa: 'Planta Baixa',
  memoralDescritivo: 'Memorial Descritivo',
  licencaAmbiental: 'Licença Ambiental',
  outorgaAnatel: 'Outorga ANATEL',
  procuracaoRepresentante: 'Procuração do Representante'
};

const infrastructureLabels = {
  hasElectricity: 'Energia Elétrica',
  hasWater: 'Água',
  hasFiber: 'Fibra Óptica',
  hasAccess24h: 'Acesso 24 horas',
  hasSecurity: 'Vigilância/Segurança',
  hasParking: 'Estacionamento'
};

function PropertyDetails() {
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  useEffect(() => {
    loadProperty();
  }, [id]);

  const loadProperty = async () => {
    try {
      const response = await propertiesAPI.getById(id);
      setProperty(response.data);
    } catch (error) {
      toast.error('Erro ao carregar dados do imóvel');
      navigate('/imoveis');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      await propertiesAPI.updateStatus(id, status);
      toast.success('Status atualizado com sucesso');
      loadProperty();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Disponível': 'success',
      'Em Negociação': 'warning',
      'Em Assinatura do Contrato': 'info',
      'Contrato Assinado': 'primary',
      'Em Licenciamento': 'warning',
      'Licença Emitida': 'success',
      'Reservado': 'info',
      'Locado': 'primary',
      'Indisponível': 'danger'
    };
    return colors[status] || 'secondary';
  };

  const handleAvailabilityChange = async (newStatus) => {
    try {
      await propertiesAPI.updateStatus(id, null, newStatus);
      toast.success('Status do terreno atualizado com sucesso');
      loadProperty();
    } catch (error) {
      toast.error('Erro ao atualizar status do terreno');
    }
  };

  const getCadastroStatusColor = (status) => {
    const colors = {
      'Pendente': 'warning',
      'Em Análise': 'info',
      'Aprovado': 'success',
      'Rejeitado': 'danger'
    };
    return colors[status] || 'secondary';
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!property) {
    return null;
  }

  const mapCenter = property.location?.coordinates 
    ? [property.location.coordinates[1], property.location.coordinates[0]]
    : [-23.5505, -46.6333];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{property.propertyName || 'Imóvel sem nome'}</h1>
          <p className="page-subtitle">
            {property.address?.city}/{property.address?.state} • {property.propertyType}
          </p>
        </div>
        <div className="btn-group">
          <button onClick={() => navigate('/imoveis')} className="btn btn-secondary">
            ← Voltar
          </button>
          <Link to={`/imoveis/${id}/editar`} className="btn btn-primary">
            ✏️ Editar
          </Link>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
        {/* Status e Informações Principais */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">📊 Status</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--gray-600)', display: 'block', marginBottom: '4px' }}>
                  Disponibilidade
                </label>
                <span className={`badge badge-${getStatusColor(property.availabilityStatus)}`}>
                  {property.availabilityStatus}
                </span>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--gray-600)', display: 'block', marginBottom: '4px' }}>
                  Status do Cadastro
                </label>
                <span className={`badge badge-${getCadastroStatusColor(property.status)}`}>
                  {property.status}
                </span>
              </div>
            </div>

            {hasPermission('Administrador', 'Analista') && (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--gray-600)', display: 'block', marginBottom: '8px' }}>
                    Alterar Status do Cadastro:
                  </label>
                  <div className="btn-group">
                    <button 
                      onClick={() => handleStatusChange('Em Análise')}
                      className="btn btn-sm btn-info"
                      disabled={property.status === 'Em Análise'}
                    >
                      Em Análise
                    </button>
                    <button 
                      onClick={() => handleStatusChange('Aprovado')}
                      className="btn btn-sm btn-success"
                      disabled={property.status === 'Aprovado'}
                    >
                      Aprovar
                    </button>
                    <button 
                      onClick={() => handleStatusChange('Rejeitado')}
                      className="btn btn-sm btn-danger"
                      disabled={property.status === 'Rejeitado'}
                    >
                      Rejeitar
                    </button>
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: '12px', color: 'var(--gray-600)', display: 'block', marginBottom: '8px' }}>
                    Alterar Status do Terreno:
                  </label>
                  <select
                    className="form-select"
                    value={property.availabilityStatus}
                    onChange={(e) => handleAvailabilityChange(e.target.value)}
                    style={{ maxWidth: '280px' }}
                  >
                    <option value="Disponível">Disponível</option>
                    <option value="Em Negociação">Em Negociação</option>
                    <option value="Em Assinatura do Contrato">Em Assinatura do Contrato</option>
                    <option value="Contrato Assinado">Contrato Assinado</option>
                    <option value="Em Licenciamento">Em Licenciamento</option>
                    <option value="Licença Emitida">Licença Emitida</option>
                    <option value="Reservado">Reservado</option>
                    <option value="Locado">Locado</option>
                    <option value="Indisponível">Indisponível</option>
                  </select>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Proprietário */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">👤 Proprietário</h3>
          </div>
          <div className="card-body">
            {property.owner ? (
              <>
                <p><strong>{property.owner.name}</strong></p>
                <p style={{ fontSize: '14px', color: 'var(--gray-600)' }}>{property.owner.type}</p>
                <div style={{ marginTop: '12px' }}>
                  <p>📧 {property.owner.email}</p>
                  <p style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                    📞 {property.owner.phone}
                    <a 
                      href={`https://wa.me/55${(property.owner.whatsapp || property.owner.phone).replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-success"
                      style={{ 
                        padding: '4px 12px', 
                        fontSize: '12px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <span style={{ fontSize: '14px' }}>💬</span> WhatsApp
                    </a>
                  </p>
                  {property.owner.whatsapp && property.owner.whatsapp !== property.owner.phone && (
                    <p style={{ marginTop: '4px', fontSize: '13px', color: 'var(--gray-500)' }}>
                      WhatsApp: {property.owner.whatsapp}
                    </p>
                  )}
                </div>
              </>
            ) : (
              <p>Proprietário não encontrado</p>
            )}
          </div>
        </div>

        {/* Dados do Imóvel */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">🏢 Dados do Imóvel</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--gray-600)' }}>Tipo</label>
                <p style={{ fontWeight: 500 }}>{property.propertyType}</p>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--gray-600)' }}>Aquisição</label>
                <p style={{ fontWeight: 500 }}>
                  {property.acquisitionType}
                  {property.acquisitionTypeOther && ` (${property.acquisitionTypeOther})`}
                </p>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--gray-600)' }}>Área Disponível</label>
                <p style={{ fontWeight: 500 }}>{property.availableArea}</p>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--gray-600)' }}>Área Total</label>
                <p style={{ fontWeight: 500 }}>{property.totalArea ? `${property.totalArea} m²` : 'Não informado'}</p>
              </div>
              {property.expectedRent && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: '12px', color: 'var(--gray-600)' }}>Valor Pretendido</label>
                  <p style={{ fontWeight: 500, color: 'var(--success)', fontSize: '18px' }}>
                    R$ {property.expectedRent.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} / mês
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Endereço */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">📍 Endereço</h3>
          </div>
          <div className="card-body">
            <p>
              {property.address?.street}, {property.address?.number || 'S/N'}
              {property.address?.complement && ` - ${property.address.complement}`}
            </p>
            <p>{property.address?.neighborhood}</p>
            <p>{property.address?.city}/{property.address?.state}</p>
            {property.address?.zipCode && <p>CEP: {property.address.zipCode}</p>}
            
            {property.location?.coordinates && (
              <a 
                href={`https://www.google.com/maps?q=${property.location.coordinates[1]},${property.location.coordinates[0]}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline"
                style={{ marginTop: '12px' }}
              >
                🗺️ Abrir no Google Maps
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Mapa */}
      <div className="card" style={{ marginTop: '20px' }}>
        <div className="card-header">
          <h3 className="card-title">🗺️ Localização no Mapa</h3>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <div style={{ height: '400px' }}>
            <MapContainer 
              center={mapCenter} 
              zoom={15} 
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {property.location?.coordinates && (
                <Marker position={[property.location.coordinates[1], property.location.coordinates[0]]}>
                  <Popup>
                    <strong>{property.propertyName || 'Imóvel'}</strong><br />
                    {property.address?.city}/{property.address?.state}<br />
                    Área: {property.availableArea}
                  </Popup>
                </Marker>
              )}
            </MapContainer>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px', marginTop: '20px' }}>
        {/* Documentos */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">📄 Documentos Disponíveis</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
              {Object.entries(documentLabels).map(([key, label]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ 
                    color: property.documents?.[key] ? 'var(--success)' : 'var(--gray-400)',
                    fontSize: '16px'
                  }}>
                    {property.documents?.[key] ? '✅' : '⬜'}
                  </span>
                  <span style={{ 
                    fontSize: '13px',
                    color: property.documents?.[key] ? 'var(--gray-800)' : 'var(--gray-500)'
                  }}>
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Infraestrutura */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">⚡ Infraestrutura</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
              {Object.entries(infrastructureLabels).map(([key, label]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ 
                    color: property.infrastructure?.[key] ? 'var(--success)' : 'var(--gray-400)',
                    fontSize: '16px'
                  }}>
                    {property.infrastructure?.[key] ? '✅' : '⬜'}
                  </span>
                  <span style={{ 
                    fontSize: '13px',
                    color: property.infrastructure?.[key] ? 'var(--gray-800)' : 'var(--gray-500)'
                  }}>
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Observações */}
      {property.notes && (
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3 className="card-title">📝 Observações</h3>
          </div>
          <div className="card-body">
            <p style={{ whiteSpace: 'pre-wrap' }}>{property.notes}</p>
          </div>
        </div>
      )}

      {/* Metadados */}
      <div className="card" style={{ marginTop: '20px' }}>
        <div className="card-body" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          fontSize: '13px', 
          color: 'var(--gray-500)' 
        }}>
          <span>
            Cadastrado por: {property.createdBy?.name || 'Sistema'} 
            em {new Date(property.createdAt).toLocaleDateString('pt-BR', { 
              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
            })}
          </span>
          <span>
            Última atualização: {new Date(property.updatedAt).toLocaleDateString('pt-BR', { 
              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
            })}
          </span>
        </div>
      </div>
    </div>
  );
}

export default PropertyDetails;
